<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate, faEye, faCalendarDay } from '@fortawesome/pro-light-svg-icons';
  // Using local API endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';
  const demoMode = import.meta.env.VITE_DEMO_MODE === 'true';

  import { formatToLocalDateTime } from '$utils/dateUtils.js';
  import { exportToExcel, generateExportFilename } from '$utils/export.js';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import SiteDetails from '$lib/SiteDetails.svelte';

  let insulinData = [];
  let isLoading = true;
  let selectedStatus = 'ALL';
  let isExporting = false;
  let startDate = '';
  let endDate = new Date().toLocaleDateString('en-CA'); // Format: YYYY-MM-DD (using en-CA locale)
  let currentPage = 1;
  let totalPages = 1;
  let pageSize = 20;
  let totalCount = 0;
  let selectedDay = new Date().toLocaleDateString('en-CA'); // Format: YYYY-MM-DD (using en-CA locale)
  let dayRange = 14; // days

  // SP-4: timezone the day filter is interpreted in (participant > site > browser).
  function participantTimezone() {
    return (
      $selectedParticipant?.timezoneCode ||
      $selectedSite?.timezoneCode ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      'UTC'
    );
  }

  const formatUnits = (value) => {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return value ?? '';
    }

    return numericValue.toFixed(2);
  };

  onMount(async () => {
    // Set default start date to 30 days before current date
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    startDate = thirtyDaysAgo.toLocaleDateString('en-CA');
    
    if ($selectedStudy) {
      fetchInsulinData(1);
    }
  });

  // Subscribe to selectedStudy changes
  $: {
    if ($selectedStudy) {
      fetchInsulinData(1);
    }
  }

  async function fetchInsulinData(page = 1) {
    try {
      isLoading = true;
      let url = `${API_BASE_URL}/forms/insulinAggregate?page=${page}&pageSize=${pageSize}&studyGUID=${$selectedStudy._id}&subjectStatus=${selectedStatus}`;

      if ($selectedParticipant) {
        url += `&subjectGUID=${$selectedParticipant._id}`;
      }

      if ($selectedSite) {
        url += `&siteGUID=${$selectedSite._id}`;
      }

      if (selectedDay && dayRange !== 0) {
        url += `&day=${selectedDay}&days=${dayRange}`;
        // SP-4: day window is evaluated in the participant's timezone; this is
        // the fallback for subjects with no TZ record.
        url += `&timezone=${encodeURIComponent(participantTimezone())}`;
      } else {
        if (startDate) {
          url += `&startDate=${startDate}`;
        }

        if (endDate) {
          url += `&endDate=${endDate}`;
        }
      }

      const res = await fetch(url);

      if (!res.ok) {
        const errorData = await res.json();
        console.error('Error response:', errorData);
        throw new Error(`Failed to fetch insulin data: ${errorData.error || res.statusText}`);
      }

      const responseData = await res.json();
      insulinData = responseData.data || [];
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;
      totalCount = responseData.totalCount || 0;
    } catch (err) {
      console.error('Error fetching insulin data:', err);
      insulinData = [];
      totalCount = 0;
    } finally {
      isLoading = false;
    }
  }

  function handleRefresh() {
    fetchInsulinData();
  }

  function handleDateChange() {
    fetchInsulinData();
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchInsulinData(newPage);
    }
  }

  function handleDayChange() {
    fetchInsulinData(1); // Reset to first page when changing filters
  }

  // Add a new function to fetch all insulin data
  async function fetchAllInsulinData() {
    try {
      let url = `${API_BASE_URL}/forms/insulinAggregate?page=1&pageSize=1000&studyGUID=${$selectedStudy._id}&subjectStatus=${selectedStatus}`;

      if ($selectedParticipant) {
        url += `&subjectGUID=${$selectedParticipant._id}`;
      }

      if ($selectedSite) {
        url += `&siteGUID=${$selectedSite._id}`;
      }

      if (selectedDay && dayRange !== 0) {
        url += `&day=${selectedDay}&days=${dayRange}`;
        // SP-4: day window is evaluated in the participant's timezone; this is
        // the fallback for subjects with no TZ record.
        url += `&timezone=${encodeURIComponent(participantTimezone())}`;
      } else {
        if (startDate) {
          url += `&startDate=${startDate}`;
        }

        if (endDate) {
          url += `&endDate=${endDate}`;
        }
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch all insulin data');
      }

      const responseData = await res.json();
      return responseData.data || [];
    } catch (err) {
      console.error('Error fetching all insulin data:', err);
      return [];
    }
  }

  // Update the handleExportExcel function to use fetchAllInsulinData
  async function handleExportExcel() {
    try {
      isExporting = true;
      
      // Fetch all data for export
      const allInsulinData = await fetchAllInsulinData();
      
      // Create formatted date string
      const now = new Date();
      const dateStr = now
        .toLocaleString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
        .replace(/,/g, '')
        .replace(/\s+/g, '')
        .replace(/:/g, '')
        .replace(/(\d{2})([A-Za-z]{3})(\d{4})_(\d{2})(\d{2})/, '$2$1$3_$4$5');

      const filename = generateExportFilename({
        protocolNumber: $selectedStudy?.protocolNumber,
        subjectId: $selectedParticipant?.subjectId,
        reportType: 'InsulinDaily'
      });

      // Transform the data to match the required format
      const formattedData = allInsulinData.map((item) => {
        return {
          Study: item.study || '',
          Protocol: item.protocol || '',
          Site: item.site || '',
          Subject: item.subject || '',
          'Day (Local)': item.date || '',
          'Form Name': item.formName || 'Insulin',
          'Units': formatUnits(item.units || 0),
          'Count': item.count || 1
        };
      });

      exportToExcel(formattedData, filename);
    } catch (error) {
      console.error('Error in handleExportExcel:', error);
    } finally {
      isExporting = false;
    }
  }
</script>

<!-- Add loading overlay for export -->
{#if isExporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

<StudyHeader />

<FeatureInfo 
  title="Daily Insulin Report" 
  description="This page displays daily insulin administration records for the selected participant or all participants in the study or selected site. 
  You can filter by start date and range to view daily insulin dosage aggregate data. 
  The report shows units administered and count of daily insulin forms submitted by each participant. 
  Data can be exported to Excel for further analysis."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{:else if $selectedSite}
  <SiteDetails showOptionsMenu={false} />
{:else if $selectedStudy}
  <StudyDetails showOptionsMenu={false} />
{/if}


<!-- Title and Filters -->
<Toolbar title="Daily Insulin Report" itemCountTitle="Records" itemCount={totalCount}>
  <div class="flex items-baseline gap-2">
    Date:
    <input
      type="date"
      bind:value={selectedDay}
      on:change={handleDayChange}
      class="h-6 w-32 px-2 border rounded dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-100 dark:border-gray-600"
    />
  </div>
  <div class="flex items-baseline gap-2">
    Range:
    <select
      bind:value={dayRange}
      on:change={handleDayChange}
      class="form-select h-6 w-24 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
    >
      <option value={0}>All</option>
      <option value={7}>7 days</option>
      <option value={14}>14 days</option>
      <option value={28}>28 days</option>
    </select>
  </div>
  {#if !$selectedParticipant}
    <div class="flex items-baseline gap-2">
      Status:
      <select
        bind:value={selectedStatus}
        on:change={() => fetchInsulinData(1)}
        class="form-select h-6 w-48 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
      >
        <option value="ALL">All</option>
        <option value="COMPD">Completed</option>
        <option value="CONS">Consented</option>
        <option value="DISC">Discontinued</option>
        <option value="INIT">Initiated</option>
        <option value="SCFA">Screen Failed</option>
        <option value="SCRE">Screened</option>
        <option value="ENRO">Screened/Enrolled</option>
      </select>
    </div>
  {/if}
  <div class="flex items-baseline">
    <button on:click={() => fetchInsulinData(1)} class="pagination" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">preparing insulin report</p>
  </div>
{:else}
  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Study</th>
          <th class="px-4 py-2 text-left bold">Protocol</th>
          <th class="px-4 py-2 text-left bold">Site</th>
          <th class="px-4 py-2 text-left bold">Subject</th>
          <th class="px-4 py-2 text-left bold">Day (Local)</th>
          <th class="px-4 py-2 text-left bold">Form Name</th>
          <th class="px-4 py-2 text-left bold">Units</th>
          <th class="px-4 py-2 text-left bold">Count</th>
        </tr>
      </thead>
      <tbody>
        {#each insulinData as item, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">{item.study}</td>
            <td class="px-4 py-2">{item.protocol}</td>
            <td class="px-4 py-2">{item.site}</td>
            <td class="px-4 py-2">{item.subject}</td>
            <td class="px-4 py-2">{item.date}</td>
            <td class="px-4 py-2">{item.formName}</td>
            <td class="px-4 py-2">{formatUnits(item.units)}</td>
            <td class="px-4 py-2">{item.count}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<!-- Add Control Buttons -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .fixed {
    position: fixed;
  }

  .inset-0 {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .bg-opacity-50 {
    --tw-bg-opacity: 0.5;
  }

  .z-50 {
    z-index: 50;
  }

  .shadow-lg {
    --tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color),
      0 4px 6px -4px var(--tw-shadow-color);
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000),
      var(--tw-shadow);
  }
</style> 